import { useEffect } from 'react'
import { useThemeStore } from '../stores/theme'

interface ThemeProviderProps {
  children: React.ReactNode
  brandColors?: {
    50?: string; 100?: string; 200?: string; 300?: string; 400?: string
    500?: string; 600?: string; 700?: string; 800?: string; 900?: string; 950?: string
  }
}

export function ThemeProvider({ children, brandColors }: ThemeProviderProps) {
  const resolvedTheme = useThemeStore((s) => s.resolvedTheme)
  const accent = useThemeStore((s) => s.accent)
  const _resolveTheme = useThemeStore((s) => s._resolveTheme)

  // Apply data-theme attribute
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolvedTheme)
  }, [resolvedTheme])

  // Apply data-accent attribute (drives the primary ramp in tokens.css)
  useEffect(() => {
    document.documentElement.setAttribute('data-accent', accent)
  }, [accent])

  // Resolve once on mount (so a persisted theme applies immediately, not
  // just on the next system change) and listen for system theme changes
  useEffect(() => {
    _resolveTheme()
    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => _resolveTheme()
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [_resolveTheme])

  // Keep the browser-chrome theme color in sync with the app theme (the
  // index.html metas only track the OS preference, not the app's choice)
  useEffect(() => {
    const color = resolvedTheme === 'dark' ? '#0c0a09' : '#ffffff'
    document
      .querySelectorAll('meta[name="theme-color"]')
      .forEach((meta) => meta.setAttribute('content', color))
  }, [resolvedTheme])

  // Inject brand color overrides
  useEffect(() => {
    if (!brandColors) return
    const entries = Object.entries(brandColors)
    if (entries.length === 0) return

    const style = document.createElement('style')
    style.id = 'brand-colors'
    const vars = entries
      .map(([shade, value]) => `  --color-primary-${shade}: ${value};`)
      .join('\n')
    style.textContent = `:root {\n${vars}\n}`

    // Replace existing brand style if any
    document.getElementById('brand-colors')?.remove()
    document.head.appendChild(style)

    return () => { style.remove() }
  }, [brandColors])

  return <>{children}</>
}
